import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Navbar } from "@/components/Navbar";
import { FieldPinPanel } from "@/components/FieldPinPanel";
import { RainAlerts } from "@/components/RainAlerts";
import { OutbreakLayer, OutbreakList } from "@/components/OutbreakLayer";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { MapPin, Crosshair, Search, X, CloudRain } from "lucide-react";
import { toast } from "sonner";

const scoreColor = (s) => (s >= 75 ? "#2D6A4F" : s >= 50 ? "#D97706" : "#B91C1C");

const RAIN_RING = { heavy: "#1D4ED8", moderate: "#60A5FA" };

const pinIcon = (score, active, rain) => {
  const ring = RAIN_RING[rain];
  return L.divIcon({
    className: "",
    iconSize: [34, 34],
    iconAnchor: [17, 34],
    html: `<div style="position:relative;width:34px;height:34px">
      ${ring ? `<div class="rain-pulse" style="position:absolute;inset:-9px;border-radius:50%;border:3px solid ${ring};opacity:.8"></div>` : ""}
      <div style="width:34px;height:34px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${scoreColor(score)};border:3px solid ${active ? "#fff" : "rgba(255,255,255,.7)"};box-shadow:0 4px 10px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center">
        <span style="transform:rotate(45deg);color:#fff;font:700 11px/1 'JetBrains Mono',monospace">${score}</span></div>
      ${ring ? `<div style="position:absolute;top:-8px;right:-10px;width:20px;height:20px;border-radius:50%;background:${ring};border:2px solid #fff;display:flex;align-items:center;justify-content:center;font-size:11px">💧</div>` : ""}
    </div>`,
  });
};

function ClickCatcher({ enabled, onPick }) {
  useMapEvents({ click: (e) => enabled && onPick(e.latlng) });
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => { if (target) map.flyTo([target.lat, target.lon], Math.max(map.getZoom(), 9), { duration: 0.8 }); }, [target, map]);
  return null;
}

export default function FieldMap() {
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [placing, setPlacing] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [rain, setRain] = useState({});
  const [outbreaks, setOutbreaks] = useState([]);
  const [showOutbreaks, setShowOutbreaks] = useState(true);

  const loadRain = () => apiClient.get("/fields/rain-alerts").then((r) => setRain(Object.fromEntries(r.data.map((a) => [a.field_id, a])))).catch(() => {});
  useEffect(() => { apiClient.get("/fields").then((r) => setFields(r.data)).catch(() => {}); loadRain(); }, []);

  const anchor = fields.find((f) => typeof f.lat === "number" && typeof f.lon === "number");
  useEffect(() => {
    if (!anchor) return;
    apiClient.get("/outbreaks", { params: { lat: anchor.lat, lon: anchor.lon, radius_km: 150 } }).then((r) => setOutbreaks(r.data)).catch(() => {});
  }, [anchor?.id, anchor?.lat, anchor?.lon]);

  const placed = useMemo(() => fields.filter((f) => typeof f.lat === "number" && typeof f.lon === "number"), [fields]);
  const unplaced = useMemo(() => fields.filter((f) => !(typeof f.lat === "number" && typeof f.lon === "number")), [fields]);
  const selected = fields.find((f) => f.id === selectedId) || null;
  const center = placed.length ? [placed[0].lat, placed[0].lon] : [10, 20];

  const saveLocation = async (field, lat, lon) => {
    try {
      const res = await apiClient.patch(`/fields/${field.id}/location`, { lat, lon });
      setFields((fs) => fs.map((f) => (f.id === field.id ? res.data : f)));
      setPlacing(null);
      setSelectedId(field.id);
      setFlyTarget({ lat, lon });
      toast.success(`${field.name} pinned on the map`);
      loadRain();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const locateByName = async (field) => {
    if (!field.location) return toast.error("This field has no location name — click on the map to place it");
    try {
      const res = await apiClient.get("/geocode", { params: { q: field.location } });
      await saveLocation(field, res.data.lat, res.data.lon);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-emerald-950 tracking-tight">Field Map</h1>
            <p className="text-stone-600 mt-1">Pin each field, then tap a pin for live weather and this month's reminders.</p>
          </div>
          {placing && (
            <div data-testid="placing-banner" className="flex items-center gap-3 bg-[#D97706] text-white rounded-full pl-4 pr-2 py-2 text-sm font-semibold">
              <Crosshair className="h-4 w-4 animate-pulse" /> Click the map to place "{placing.name}"
              <button data-testid="placing-cancel-btn" onClick={() => setPlacing(null)} className="h-7 w-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-[1fr_380px] gap-6">
          <div data-testid="field-map" className={`rounded-3xl overflow-hidden border border-stone-200 h-[520px] lg:h-[640px] ${placing ? "ring-4 ring-[#D97706]/50 cursor-crosshair" : ""}`}>
            <MapContainer center={center} zoom={placed.length ? 6 : 2} className="h-full w-full" scrollWheelZoom>
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <ClickCatcher enabled={!!placing} onPick={(ll) => saveLocation(placing, ll.lat, ll.lng)} />
              <FlyTo target={flyTarget} />
              {showOutbreaks && <OutbreakLayer outbreaks={outbreaks} />}
              {placed.map((f) => (
                <Marker key={f.id} position={[f.lat, f.lon]} icon={pinIcon(Math.round(f.analysis?.soil_health?.score || 0), f.id === selectedId, rain[f.id]?.level)}
                  eventHandlers={{ click: () => { setSelectedId(f.id); setPlacing(null); } }} />
              ))}
            </MapContainer>
          </div>

          <aside className="space-y-4">
            {selected ? (
              <FieldPinPanel field={selected} rain={rain[selected.id]} onClose={() => setSelectedId(null)} onMove={() => { setPlacing(selected); setSelectedId(null); }} />
            ) : (
              <>
                <RainAlerts alerts={Object.values(rain)} onPick={(id) => { const f = fields.find((x) => x.id === id); if (f) { setSelectedId(id); setFlyTarget({ lat: f.lat, lon: f.lon }); } }} />
                {anchor && <OutbreakList outbreaks={outbreaks} enabled={showOutbreaks} onToggle={setShowOutbreaks} onPick={(o) => { setShowOutbreaks(true); setFlyTarget({ lat: o.lat, lon: o.lon }); }} />}
                <div className="bg-white rounded-2xl border border-dashed border-stone-300 p-6 text-center" data-testid="map-hint">
                  <MapPin className="h-8 w-8 text-[#2D6A4F] mx-auto mb-2" />
                  <p className="text-sm text-stone-600">{placed.length ? "Tap a pin to see live weather and reminders for that field." : "No fields on the map yet — place one below."}</p>
                </div>
              </>
            )}

            {placed.length > 0 && !selected && (
              <div className="bg-white rounded-2xl border border-stone-200 p-4" data-testid="placed-list">
                <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">On the map</div>
                <ul className="space-y-1">
                  {placed.map((f) => (
                    <li key={f.id}>
                      <button data-testid={`placed-field-${f.id}`} onClick={() => { setSelectedId(f.id); setFlyTarget({ lat: f.lat, lon: f.lon }); }}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-[#EAF6EF] text-left">
                        <span className="font-semibold text-emerald-950 truncate flex items-center gap-1.5">{f.name}{rain[f.id]?.level && rain[f.id].level !== "none" && <CloudRain className={`h-3.5 w-3.5 ${rain[f.id].level === "heavy" ? "text-blue-700" : "text-blue-400"}`} />}</span>
                        <span className="font-mono-tech text-xs font-bold px-2 py-0.5 rounded-md text-white" style={{ backgroundColor: scoreColor(Math.round(f.analysis?.soil_health?.score || 0)) }}>{Math.round(f.analysis?.soil_health?.score || 0)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {unplaced.length > 0 && (
              <div className="bg-white rounded-2xl border border-stone-200 p-4" data-testid="unplaced-list">
                <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Not on the map yet</div>
                <ul className="space-y-2">
                  {unplaced.map((f) => (
                    <li key={f.id} data-testid={`unplaced-field-${f.id}`} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-[#FAF9F6]">
                      <div className="min-w-0"><div className="font-semibold text-emerald-950 truncate">{f.name}</div><div className="text-xs text-stone-500 truncate">{f.location || "no location name"}</div></div>
                      <div className="flex gap-1 shrink-0">
                        {f.location && <Button data-testid={`locate-field-${f.id}`} size="sm" variant="outline" onClick={() => locateByName(f)} className="h-8 border-stone-300" title="Find by location name"><Search className="h-3.5 w-3.5" /></Button>}
                        <Button data-testid={`place-field-${f.id}`} size="sm" onClick={() => { setPlacing(f); setSelectedId(null); }} className={`h-8 text-white ${placing?.id === f.id ? "bg-[#78350F]" : "bg-[#D97706] hover:bg-[#78350F]"}`}><Crosshair className="h-3.5 w-3.5 mr-1" /> Place</Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {fields.length === 0 && (
              <Button onClick={() => navigate("/app/analyze")} className="w-full bg-[#2D6A4F] hover:bg-[#143D29] text-white rounded-xl">Save your first field</Button>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}
